import { ReactNode, useCallback, useEffect, useRef } from "react";
import { getApiFailureMessage, showToastMessage, uuidv4 } from "../../../../utils/commonFunctions";
import { getEnvVar } from "../../../../envUtils";
import useSocket from "../hooks/useSocket";
import useNavigation from "../hooks/useNavigation";
import usePresimApis from "../hooks/usePresimApis";
import useForm from "../hooks/useForm";
import useScenarioResponse from "../hooks/useScenarioResponse";
import { ScenarioResponseType } from "../../types";
import useHwilApi from "../hooks/useHwilApi";
import useLoader from "../hooks/useLoader";
import useBaseApi from "../hooks/useBaseApi";
import useViewer from "../hooks/useViewer";
import useStepper from "../hooks/useStepper";
import SimulateService from "../../../../service/SimulateService";
import MissionService from "../../../../service/MissionService";
import { formatOrbitalInsightsData, formatPresimData, getFormattedFaultsPlanData, getFormattedScheduleData } from "../utils/helperFunction";
import { getOrbitalInsights } from "../../../CommonComponents/orbital-insights/api";
import { createSatellitePacket } from "../utils/viewer-packets";
import { SCENARIO_STEPS } from "../../constants";
import useTimelineFilter from "../hooks/useTimelineFilter";
import useSelectedGroupSatellite from "../hooks/useSelectedGroupSatellite";
import useSatelliteGroupsAdd from "../hooks/useSatelliteGroupsAdd";
import {
    getScenarioSetupMessage,
    getScenarioSetupStatus,
    getSatelliteGroupsAddEventType,
    getSatelliteGroupsAddScenarioId,
    SCENARIO_SETUP_STATUS,
    SCENARIO_SATELLITE_GROUPS_ADD_EVENT
} from "../utils/satellite-groups-add";


interface SocketProviderProps {
    children: ReactNode;
}

const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const { setLoading } = useLoader();
    const { socket, setSocket } = useSocket();
    const { getScenarioDetails } = useBaseApi()
    const { formData, setFormData } = useForm()
    const { missionId, scenarioId } = useNavigation();
    const { resetTimelineFilter } = useTimelineFilter()
    const { getRemoteAppConnectionStatus } = useHwilApi()
    const { selectedGroupSatellite } = useSelectedGroupSatellite();
    const { getPresimData, stopAllPresimLoaders } = usePresimApis();
    const { scenarioResponse, setScenarioResponse } = useScenarioResponse();

    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const sessionIdRef = useRef<string>(uuidv4());
    const satelliteGroupsAddCompletionInProgressRef = useRef(false);
    const scheduleGenerationCompletionInProgressRef = useRef(false);
    // New ref to track if the component is mounted
    const isComponentMounted = useRef(true);

    const { viewer } = useViewer();
    const { updateScenarioStep } = useStepper();
    const { completeSatelliteGroupsAdd, failSatelliteGroupsAdd, setPlottingLoader } = useSatelliteGroupsAdd()

    const create_socket = (sessionId: string): WebSocket | null => {
        try {
            // Ensure REACT_APP_WEBSOCKET_BACKEND_BASE_URL is correctly set in your .env file
            // e.g., REACT_APP_WEBSOCKET_BACKEND_BASE_URL=ws://localhost:8000/
            const wsUrl = `${getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL')}test/scenario/prediction-deploy-event?resourceId=${scenarioId}&sessionId=${sessionId}`;
            console.log(`Attempting to create WebSocket`);
            return new WebSocket(wsUrl);
        } catch (error) {
            console.error("Failed to create WebSocket instance:", error); // Use console.error for errors
            return null;
        }
    };

    const connectSocket = useCallback(() => {
        // If a socket already exists and is open, or if component is unmounted, do nothing
        if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
            console.log("Socket already exists or is connecting. Skipping new connection attempt.");
            return;
        }
        if (!isComponentMounted.current) {
            console.log("Component is unmounted. Aborting socket connection attempt.");
            return;
        }

        const token = sessionStorage.getItem("authentication");
        if (!token) {
            console.warn("Authentication token not found. Cannot connect WebSocket.");
            return;
        }

        // Clear any pending reconnect timeout before creating a new socket
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        const ws = create_socket(sessionIdRef.current);
        wsRef.current = ws;
        if (!ws) {
            console.error("WebSocket instance is null. Cannot proceed with connection.");
            return;
        }

        ws.onopen = () => {
            if (!isComponentMounted.current) {
                console.log("Component unmounted during connection. Closing socket.");
                ws.close();
                return;
            }
            ws.send(JSON.stringify({ type: 'authorization', token }));
            console.log("✅ Socket connected");
            setSocket(ws);

            // Clear any previous ping interval
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
            }
            // Start heartbeat
            pingIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'ping' }));
                } else {
                    // If socket is not open, clear interval and let onclose handle reconnect
                    console.log("Socket not open during ping. Clearing ping interval.");
                    clearInterval(pingIntervalRef.current!);
                    pingIntervalRef.current = null;
                }
            }, 25000);
        };

        ws.onclose = (event) => {
            console.log(`❌ Socket disconnected. Code: ${event.code}, Reason: ${event.reason}`);
            clearInterval(pingIntervalRef.current!);
            pingIntervalRef.current = null;
            setSocket(null); // Clear global socket state

            // Only attempt to reconnect if the component is still mounted
            if (isComponentMounted.current) {
                console.log("🔁 Attempting to reconnect in 3 seconds...");
                reconnectTimeoutRef.current = setTimeout(() => {
                    // Only reconnect if the document is visible
                    if (document.visibilityState === 'visible') {
                        connectSocket();
                    } else {
                        console.log("Tab not visible. Reconnection deferred until tab becomes active.");
                        // If not visible, the visibilitychange listener will handle reconnect
                    }
                }, 3000);
            } else {
                console.log("Component unmounted. Aborting reconnect.");
            }
        };

        ws.onerror = (err) => {
            console.error("⚠️ Socket error:", err);
            // Force close to trigger onclose and potential reconnect if mounted
            ws.close();
        };
    }, [scenarioId, setSocket]); // Add setSocket to dependencies if it's from a custom hook

    useEffect(() => {
        // Set component mounted status to true on mount
        isComponentMounted.current = true;

        if (!scenarioId) {
            console.warn("scenarioId is not available. Skipping socket connection.");
            return;
        }

        connectSocket(); // Initial connection attempt

        const handleVisibilityChange = () => {
            // Only attempt to connect if component is mounted, tab is visible, and no active socket
            if (isComponentMounted.current && document.visibilityState === 'visible' && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
                console.log("👀 Tab active and component mounted — retrying socket connection...");
                connectSocket();
            } else if (!isComponentMounted.current) {
                console.log("Visibility change detected but component is unmounted. Ignoring.");
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup function for useEffect
        return () => {
            console.log("🧹 Cleaning up WebSocket connection...");
            isComponentMounted.current = false; // Mark component as unmounted

            // Clear all pending timeouts and intervals
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            // Close the WebSocket connection if it exists and is open/connecting
            if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
                wsRef.current.close(1000, "Component unmounted"); // Code 1000 for normal closure
                console.log("WebSocket closed due to component unmount.");
            }
            wsRef.current = null; // Clear the ref
            setSocket(null); // Clear global socket state

            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [scenarioId, connectSocket]); // Add connectSocket to dependencies as it's a useCallback

    const handleScheduleGeneration = async (data: any) => {
        const message = getScenarioSetupMessage(data)
        const setupStatus = getScenarioSetupStatus(data)
        const eventScenarioId = getSatelliteGroupsAddScenarioId(data)
        if (eventScenarioId && eventScenarioId !== scenarioId) return
        if (setupStatus) {
            setScenarioResponse((prev) => prev ? {
                ...prev,
                setupStatus,
                setupStatusMessage: message
            } : prev)
        }

        if (
            setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_SUBMITTED ||
            setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_IN_PROGRESS
        ) {
            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev.assets,
                    schedule: {
                        ...prev.assets.schedule,
                        status: 'running'
                    }
                }
            }))
            setLoading((prev) => ({
                ...prev,
                execution: 'Generating',
                executionMessage: message || prev.executionMessage || false
            }))
        } else if (setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_FAILED) {
            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev.assets,
                    schedule: {
                        ...prev.assets.schedule,
                        status: 'invalidated'
                    }
                }
            }))
            setLoading((prev) => ({ ...prev, execution: false, executionMessage: false }))
            if (message) showToastMessage('error', message)
        } else if (setupStatus === SCENARIO_SETUP_STATUS.ONBOARD_SCHEDULE_GENERATE_COMPLETED) {
            if (scheduleGenerationCompletionInProgressRef.current) return
            scheduleGenerationCompletionInProgressRef.current = true
            try {
                const scheduleResp = await SimulateService.generateImportedSchedule(missionId, scenarioId)
                const schedules = getFormattedScheduleData(scheduleResp.data)
                const orbitalInsightsResponse = await getOrbitalInsights(missionId, scenarioId!)
                const orbitalInsights = formatOrbitalInsightsData(orbitalInsightsResponse)
                const notSameTimeDuration = scenarioResponse?.simulationStartTime !== formData.schedule.simulationStartTime || scenarioResponse?.simulationDuration !== formData.schedule.simulationDuration
                let updatedFaultPlans: any = null
                if (notSameTimeDuration) {
                    const faultBody = formData?.satellites.map(satellite => ({
                        satelliteGroupId: satellite?.groupId,
                        faultPlanId: ''
                    }))
                    const faultResp = await MissionService.postFaultScheduleData(missionId, scenarioId, faultBody)
                    updatedFaultPlans = getFormattedFaultsPlanData(faultResp?.data?.data)
                }
                const scenario = await getScenarioDetails(missionId, scenarioId as string)
                const selectedGroup = scenario?.satelliteGroups[0]
                const selectedSatellite = selectedGroupSatellite?.satelliteId || selectedGroup?.satellites[0].satelliteId
                setScenarioResponse(scenario)
                resetTimelineFilter(schedules, selectedSatellite)
                setFormData((prev) => ({
                    ...prev,
                    faults: notSameTimeDuration ? [] : prev?.faults,
                    assets: {
                        ...prev?.assets,
                        presim: {
                            ...prev?.assets?.presim,
                            data: formatPresimData(prev?.assets?.presim?.data, scenario?.satelliteGroups)
                        },
                        schedule: {
                            status: 'completed',
                            data: schedules,
                            allSchedules: scheduleResp.data
                        },
                        faultPlans: notSameTimeDuration ? {
                            status: 'invalidated',
                            data: updatedFaultPlans
                        } : prev?.assets?.faultPlans,
                        orbitalInsights: {
                            status: orbitalInsights ? 'completed' : 'idle',
                            data: orbitalInsights
                        }
                    }
                }))
                if (notSameTimeDuration) {
                    await createSatellitePacket(viewer, scenarioId, missionId, selectedSatellite)
                }
                updateScenarioStep(SCENARIO_STEPS.FAULTS)
            } catch (error) {
                setFormData((prev) => ({
                    ...prev,
                    assets: {
                        ...prev.assets,
                        schedule: {
                            ...prev.assets.schedule,
                            status: 'invalidated'
                        }
                    }
                }))
                showToastMessage('error', getApiFailureMessage(error, 'Fetch'))
            } finally {
                scheduleGenerationCompletionInProgressRef.current = false
                setLoading((prev) => ({ ...prev, execution: false, executionMessage: false }))
            }
        }
    }



    const handlePresimData = async (data: any, scenarioId: string, scenarioResponse: ScenarioResponseType) => {
        try {
            if (data?.["status"] === "Done") {
                const satelliteId = data?.satelliteId
                const previousPresimData = formData.assets.presim.data!
                const response = await getPresimData(missionId, scenarioId, scenarioResponse, previousPresimData, satelliteId)
                setFormData((prev) => ({
                    ...prev,
                    assets: {
                        ...prev.assets,
                        presim: {
                            ...prev.assets.presim,
                            data: {
                                ...prev.assets.presim.data,
                                ...response
                            }
                        },
                    }
                }))
            }
            else if (data?.["status"] === "Error") {
                showToastMessage("error", "Pre Sim Analysis Failed. Try Again.");
                stopAllPresimLoaders()
            }
        } catch (error) {
            showToastMessage("error", "Pre Sim Analysis Failed. Try Again.");
            stopAllPresimLoaders()
            console.error("Error parsing message:", error, data);
        }
    }

    const handleHwilDeploy = async (data: any) => {
        const status = data?.['status']
        if (status === 'COMPLETED') {
            const scenario = await getScenarioDetails(missionId, scenarioId as string)
            setScenarioResponse(scenario)
            await getRemoteAppConnectionStatus(false)
        } else if (status === 'FAILED') {
            setLoading((prev) => ({
                ...prev,
                execution: false
            }))
            showToastMessage("error", "Failed to Deploy Scenario")
        }
        return
    }

    const handleSatelliteGroupsAdd = async (data: any) => {
        const message = getScenarioSetupMessage(data)
        const setupStatus = getScenarioSetupStatus(data)
        const eventScenarioId = getSatelliteGroupsAddScenarioId(data)
        if (eventScenarioId && eventScenarioId !== scenarioId) return
        if (setupStatus) {
            setScenarioResponse((prev) => prev ? {
                ...prev,
                setupStatus,
                setupStatusMessage: message
            } : prev)
        }

        if (
            setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_SUBMITTED ||
            setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_IN_PROGRESS
        ) {
            setPlottingLoader(true)
            setLoading((prev) => ({
                ...prev,
                execution: 'Plotting',
                executionMessage: message || prev.executionMessage || false
            }))
        } else if (setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_COMPLETED) {
            if (satelliteGroupsAddCompletionInProgressRef.current) return
            satelliteGroupsAddCompletionInProgressRef.current = true
            try {
                await completeSatelliteGroupsAdd()
            } finally {
                satelliteGroupsAddCompletionInProgressRef.current = false
            }
        } else if (setupStatus === SCENARIO_SETUP_STATUS.SATELLITE_GROUPS_ADD_FAILED) {
            failSatelliteGroupsAdd(message)
        }
    }

    useEffect(() => {
        if (scenarioId && missionId && socket) {
            socket.onmessage = async (event) => {
                let data: any = null
                try {
                    data = JSON.parse(event.data || '{}');
                } catch (error) {
                    console.error("Error parsing socket message:", error, event.data);
                    return
                }
                const eventKey = getSatelliteGroupsAddEventType(data)
                const deploymentType = scenarioResponse?.scenarioDeploymentType
                if (eventKey === SCENARIO_SATELLITE_GROUPS_ADD_EVENT) {
                    await handleSatelliteGroupsAdd(data)
                } else if (!scenarioResponse) {
                    return
                } else if (eventKey === "SCENARIO_PREDICTION") {
                    await handlePresimData(data, scenarioId, scenarioResponse)
                } else if (eventKey === "SCENARIO_DEPLOYMENT" && deploymentType === 'HARDWARE_IN_LOOP') {
                    await handleHwilDeploy(data)
                } else if (eventKey === "SCENARIO_SCHEDULE_GENERATION") {
                    await handleScheduleGeneration(data)
                }
            };
        }
    }, [socket, missionId, scenarioId, scenarioResponse, formData])

    return children;
}
export default SocketProvider
